import { useEffect, useState, useRef, useCallback, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";

interface TourTooltipProps {
  target: string;
  title: string;
  content: string;
  placement: "top" | "bottom" | "left" | "right";
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
  showDontShowAgain?: boolean | undefined;
  dontShowAgain: boolean;
  onDontShowAgainChange: (v: boolean) => void;
  padding?: number | undefined;
  /** Replaces the Next button when challenge mode is active */
  challengeSlot?: React.ReactNode | undefined;
}

interface Position {
  top: number;
  left: number;
}

const TOOLTIP_WIDTH = 320;
const TOOLTIP_GAP = 12;
const VIEWPORT_MARGIN = 16;

/** If target covers >70% of viewport in either dimension, treat as full-page */
function isFullPageTarget(rect: DOMRect): boolean {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return rect.width > vw * 0.7 && rect.height > vh * 0.5;
}

function centerOnScreen(tooltipHeight: number): Position {
  return {
    top: Math.max(VIEWPORT_MARGIN, (window.innerHeight - tooltipHeight) / 2),
    left: Math.max(VIEWPORT_MARGIN, (window.innerWidth - TOOLTIP_WIDTH) / 2),
  };
}

function computePosition(
  targetRect: DOMRect,
  placement: "top" | "bottom" | "left" | "right",
  padding: number,
  tooltipHeight: number,
): Position {
  // Full-page targets (welcome/congrats steps) → center
  if (isFullPageTarget(targetRect)) {
    return centerOnScreen(tooltipHeight);
  }

  const padded = {
    top: targetRect.top - padding,
    left: targetRect.left - padding,
    width: targetRect.width + padding * 2,
    height: targetRect.height + padding * 2,
    bottom: targetRect.bottom + padding,
    right: targetRect.right + padding,
  };

  let top = 0;
  let left = 0;

  switch (placement) {
    case "bottom":
      top = padded.bottom + TOOLTIP_GAP;
      left = padded.left + padded.width / 2 - TOOLTIP_WIDTH / 2;
      break;
    case "top":
      top = padded.top - TOOLTIP_GAP - tooltipHeight;
      left = padded.left + padded.width / 2 - TOOLTIP_WIDTH / 2;
      break;
    case "right":
      top = padded.top + padded.height / 2 - tooltipHeight / 2;
      left = padded.right + TOOLTIP_GAP;
      break;
    case "left":
      top = padded.top + padded.height / 2 - tooltipHeight / 2;
      left = padded.left - TOOLTIP_WIDTH - TOOLTIP_GAP;
      break;
  }

  // Clamp within viewport
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;
  if (left + TOOLTIP_WIDTH > vw - VIEWPORT_MARGIN) left = vw - TOOLTIP_WIDTH - VIEWPORT_MARGIN;
  if (top < VIEWPORT_MARGIN) top = VIEWPORT_MARGIN;
  if (top + tooltipHeight > vh - VIEWPORT_MARGIN) top = vh - tooltipHeight - VIEWPORT_MARGIN;

  return { top, left };
}

export function TourTooltip({
  target,
  title,
  content,
  placement,
  currentStep,
  totalSteps,
  onNext,
  onPrev,
  onSkip,
  isFirstStep,
  isLastStep,
  showDontShowAgain,
  dontShowAgain,
  onDontShowAgainChange,
  padding = 8,
  challengeSlot,
}: TourTooltipProps) {
  const { t } = useTranslation();
  const [pos, setPos] = useState<Position>({ top: 0, left: 0 });
  const [visible, setVisible] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const measuredHeight = useRef(300); // sensible default

  // Measure tooltip height after render
  useLayoutEffect(() => {
    if (tooltipRef.current) {
      measuredHeight.current = tooltipRef.current.offsetHeight;
    }
  });

  const updatePosition = useCallback(() => {
    const h = measuredHeight.current;
    const el = document.querySelector(`[data-tour="${target}"]`);
    if (el) {
      const rect = el.getBoundingClientRect();
      setPos(computePosition(rect, placement, padding, h));
    } else {
      setPos(centerOnScreen(h));
    }
  }, [target, placement, padding]);

  useEffect(() => {
    setVisible(false);
    updatePosition();
    const timer = setTimeout(() => setVisible(true), 50);

    let rafId: number;
    const poll = () => {
      updatePosition();
      rafId = requestAnimationFrame(poll);
    };
    rafId = requestAnimationFrame(poll);

    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(rafId);
    };
  }, [target, updatePosition]);

  const translatedTitle = t(title);
  const translatedContent = t(content);

  return (
    <div
      ref={tooltipRef}
      className={`fixed z-[202] transition-all duration-300 ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`}
      style={{
        top: pos.top,
        left: pos.left,
        width: TOOLTIP_WIDTH,
        maxHeight: `calc(100vh - ${VIEWPORT_MARGIN * 2}px)`,
        overflowY: "auto",
      }}
    >
      <div className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-primary-600 px-4 py-3">
          <h3 className="text-sm font-semibold text-white">{translatedTitle}</h3>
          <p className="text-xs text-primary-100 mt-0.5">
            {t("tour.stepOf", { current: currentStep + 1, total: totalSteps })}
          </p>
        </div>

        {/* Body */}
        <div className="px-4 py-3">
          <p className="text-sm text-gray-600 leading-relaxed">{translatedContent}</p>
        </div>

        {/* Don't show again checkbox (first step or skip) */}
        {showDontShowAgain && (
          <div className="px-4 pb-2">
            <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(e) => onDontShowAgainChange(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 h-3.5 w-3.5"
              />
              {t("tour.dontShowAgain")}
            </label>
          </div>
        )}

        {/* Progress dots */}
        <div className="flex justify-center gap-1 px-4 py-2">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-200 ${
                i === currentStep
                  ? "w-4 bg-primary-500"
                  : i < currentStep
                    ? "w-1.5 bg-primary-300"
                    : "w-1.5 bg-gray-200"
              }`}
            />
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50">
          <button
            onClick={onSkip}
            className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
          >
            {t("tour.skip")}
          </button>

          <div className="flex items-center gap-2">
            {!isFirstStep && (
              <button
                onClick={onPrev}
                className="px-3 py-1.5 text-xs font-medium text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t("tour.back")}
              </button>
            )}

            {challengeSlot || (
              <button
                onClick={isLastStep ? onSkip : onNext}
                className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
              >
                {isLastStep ? t("tour.finish") : t("tour.next")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
